/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { Payload, QoE } from '@tivocorp/tivo-webapp-loggers';
import { AiringOffer } from 'client-code/airing';
import { TransportEncodingType } from 'client-code/video-provider-configurations';
import {
    ASSETBOUNDS,
    DASH_TRANSPORT_STREAM,
    HLS_TRANSPORT_STREAM,
    MEDIA_HEALTH_EVENTS_INTERVAL,
    MILLISECONDS_IN_A_SECOND,
    SESSION_TYPE,
    SMOOTH_TRANSPORT_STREAM,
    STREAM_TYPE,
    VIDEO_PLAYER,
    VIDEO_SOURCE,
} from '../../../config';
import { IQoEMetrics } from './IQoEMetrics';
import { IQoEContent } from './IQoEContent';
import { IQoEState } from './IQoEState';
import { Log } from '../Log';
import { VideoStreamingState } from './VideoStreamingState';
import { Channel } from '../../entities/Channel';
import { DeviceModel } from '../../device/DeviceModel';
import { IQoEConfiguration } from './IQoEConfiguration';
import { QoEConnectionModeType } from './QoEConnectionModeType';
import { QoEContainerFormatType } from './QoEContainerFormatType';
import { QoERegionModeType } from './QoERegionModeType';
import { QoEStreamingProtocolType } from './QoEStreamingProtocolType';
import { UniqueIdentifier } from '../../utils/UniqueIdentifier';
import { IQoEStatus } from './IQoEStatus';
import { Constants } from '../../constants';
import { AppUtils } from '../../AppUtils';
import { TOfferPreviewCustomData } from '../../types';

const crypto = require('crypto');

type Profile = {
    bandwidth: number;
    height: number;
    playTime: number;
    ratio: number;
    width: number;
};

export type EndReasonData = {
    type: string;
    payload: IQoEStatus;
};

export class QoEAppLogger {
    private static TAG = 'QoEAppLogger';

    public static sessionId: string = '';

    private static trickplayStartTime: number = 0;

    static PlaybackEndReasonType = {
        ADULT_CONTENT: 'ADULT_CONTENT',
        BLACKLIST: 'BLACKLIST',
        CHANNEL_CHANGE: 'CHANNEL_CHANGE',
        DEVICE_RESTRICTED: 'DEVICE_RESTRICTED',
        END_OF_CONTENT: 'END_OF_CONTENT',
        ENDED_BY_USER: 'ENDED_BY_USER',
        LOCATION_ERROR: 'LOCATION_ERROR',
        LOST_NETWORK: 'LOST_NETWORK',
        OUT_OF_HOME: 'OUT_OF_HOME',
        OUT_OF_REGION: 'OUT_OF_REGION',
        PLAYER_ERROR: 'PLAYER_ERROR',
        SESSION_ERROR: 'SESSION_ERROR',
        UNSUBSCRIBED: 'UNSUBSCRIBED',
        VISIBILITY_BEHIND: 'VISIBILITY_BEHIND',
        PLAYLIST_URI_FROM_SESSION_MANAGER: 'PLAYLIST_URI_FROM_SESSION_MANAGER',
    };

    static ErrorLocValues = {
        CLIENT: 'CLIENT',
        CONTENT: 'CONTENT',
        LOCATION: 'LOCATION',
        SESSION: 'SESSION',
    };

    static StreamEndReason = {
        AUDIO_TRACK_CHANGED: 'AUDIO_TRACK_CHANGED',
    };

    static PlaybackEndStatus = {
        CANCEL: 'CANCEL',
        SUCCESS: 'SUCCESS',
        FAIL: 'FAIL',
    };

    public static mediaStatsDefautValues: IQoEMetrics = {
        avgVideoBitrate: 0,
        avgNetworkBitrate: 0,
        networkBitrate: 0,
        playPosition: 0,
        profileShiftCount: 0,
        videoBufferAvailable: 0,
        videoBufferingTime: 0,
        videoBufferingCount: 0,
        videoBitrate: 0,
        videoFramesPresented: 0,
        videoFramesDropped: 0,
        videoPlayingTime: 0,
        videoPlayingCount: 0,
        videoPausingTime: 0,
        videoPausingCount: 0,
        videoPlayingStartTime: 0,
        videoStartTime: 0,
        videoStallingCount: 0,
        videoStallingTime: 0,
        videoTrickplayingTime: 0,
        videoTrickplayingCount: 0,
    };

    public static mediaStats: IQoEMetrics = {
        ...QoEAppLogger.mediaStatsDefautValues,
    };

    private static contentDefaultValues: IQoEContent = {
        audioCodec: '',
        channelId: '',
        channelCallSign: '',
        channelName: '',
        channelNum: '',
        collectionId: '',
        collectiontitle: '',
        contentId: '',
        contentTitle: '',
        maxRes: '',
        videoCodec: '',
    };

    private static content: IQoEContent = {
        ...QoEAppLogger.contentDefaultValues,
    };

    private static stateDefaultValues: IQoEState = {
        startTime: 0,
        streamingState: VideoStreamingState.STREAMING_STATE_PREFLIGHT,
    };

    private static state: IQoEState = {
        ...QoEAppLogger.stateDefaultValues,
    };

    private static configuration: IQoEConfiguration = {
        assetBounds: ASSETBOUNDS,
        connectionMode: QoEConnectionModeType.UNKNOWN,
        connection: '',
        regionMode: QoERegionModeType.UNKNOWN,
        sessionType: SESSION_TYPE,
        streamType: STREAM_TYPE,
        streamingProtocol: '',
        videoPlayer: VIDEO_PLAYER,
        videoSource: VIDEO_SOURCE,
    };

    private static endStatus: IQoEStatus = {
        errorCode: '',
        errorLoc: '',
        errorMsg: '',
        errorNative: '',
        errorType: '',
        errorUrl: '',
        userFinalCodes: '',
        userTerminalCodes: '',
        doneReason: '',
        status: '',
        reason: '',
    };

    public static PlayerEvents = {
        PLAY: 'play',
        PAUSE: 'pause',
        BUFFERING: 'buffering',
        STALLED: 'stalled',
        CHANGED_AD_TRACK: 'nonCollectibleAction',
        RATE_CHANGE: 'profileShiftCount',
    };

    public static ErrorType = {
        ERROR_STATUS: 'ERROR_STATUS',
        REASON: 'REASON',
        USER_CODE: 'USER_CODE',
    };

    public static PlaybackEndReason = {
        DEFAULT: '',
        USER_CLOSED_THE_VIDEO_WINDOW: 'User closed the video window',
        USER_EXITED_FROM_LIVE_TV: 'User exited from live TV',
        USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED:
            'User move to Live TV from Guide and channel was changed',
        USER_TUNED_TO_NEXT_CHANNEL: 'User tuned to next channel',
        USER_TUNED_TO_OTHER_CHANNEL_BY_ENTERING_CHANNEL_NUMBER:
            'User tuned to other channel by entering channel number',
        USER_TUNED_TO_PREVIOUS_CHANNEL: 'User tuned to previous channel',
    };

    private static previousPlayerEvent: Event;

    private static isProgramBoundary: boolean;

    private static isWatchContentStarted = false;

    private static isStreamingStarted = false;

    private static qoeStartTime: number;

    public static profiles: Profile[] = [];

    public static watchContentStartTime: number = 0;

    public static mediaHealthStartIntervalTime: number = 0;

    public static shakaPlayerInstance: any;

    public static isAdaptationEventFirstTime: boolean = false;

    public static qoeMediaHealthTimer;

    public static sessionCount = 0;

    public static watchContentSessionCount = 0;

    public static streamingSessionCount = 0;

    private static watchContentEndReason: string = this.PlaybackEndReason.DEFAULT;

    static lastTunedChannel: string | undefined = '';

    private static channel: Channel;

    private static airingOffers: AiringOffer[];

    // *****************************************************************

    public static createAppSessionId() {
        // Application execution session identifier
        QoEAppLogger.sessionId = crypto
            .createHash('sha1')
            .update(UniqueIdentifier.uuid4(), 'utf-8')
            .digest('hex')
            .substring(0, 20);
    }

    /** *****************************************************************
     * ////////////////////// QoE Events //////////////////////////////////////////
     * applicationStart
     * watchContentStart
     * streamingStart
     * sessionCreated
     * videoPlayerOpenStream
     * firstFrame
     * mediaHealth
     * streamingEnd
     * watchContentEnd
     */

    static logApplicationStart = () => {
        QoEAppLogger.qoeStartTime = Date.now();
        QoE.logApplicationStart();
    };

    static updateWatchContentUniqueId = (payload: Payload) => {
        payload.add(
            'watchContentTransaction',
            `${QoEAppLogger.sessionId}_${QoEAppLogger.watchContentSessionCount}`,
        );
    };

    static updateStreamingSessionUniqueId = (payload: Payload) => {
        payload.add(
            'streamingSessionTransaction',
            `${QoEAppLogger.sessionId}_${QoEAppLogger.streamingSessionCount}`,
        );
    };

    static setShakaPlayerInstance = (instance: any) => {
        QoEAppLogger.shakaPlayerInstance = instance;
    };

    static getPlayerStats = () => {
        return QoEAppLogger.shakaPlayerInstance?.getPlayerStats() || null;
    };

    public static logWatchContentStart = (
        channel?: Channel,
        airingOffer?: AiringOffer[],
        vodPreviewData?: TOfferPreviewCustomData,
    ) => {
        if (channel) {
            QoEAppLogger.channel = channel;
            if (airingOffer && airingOffer?.length > 0) {
                QoEAppLogger.lastTunedChannel = channel.channelNumber;
                QoEAppLogger.airingOffers = airingOffer;
            }
        }

        if (vodPreviewData) {
            QoEAppLogger.updateContentPayload(QoEAppLogger.channel, [], vodPreviewData);
        } else {
            QoEAppLogger.updateContentPayload(QoEAppLogger.channel, QoEAppLogger.airingOffers);
        }
        if (!QoEAppLogger.isWatchContentStarted) {
            QoEAppLogger.watchContentSessionCount = QoEAppLogger.sessionCount;
            QoEAppLogger.sessionCount += 1;
            if (QoEAppLogger.isProgramBoundary) {
                QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;
            } else {
                QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_PREFLIGHT;
            }
            QoEAppLogger.state.startTime = Date.now() / Constants.MILLISECOND_IN_ONE_SECOND;
            QoEAppLogger.watchContentStartTime =
                QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND;
            let payload = QoEAppLogger.getContentPayload();
            payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
            payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
            delete payload.getProperties().streamingSessionTransaction;
            QoEAppLogger.updateWatchContentUniqueId(payload);
            QoE.logWatchContentStart(payload);
            QoEAppLogger.isProgramBoundary = false;
            QoEAppLogger.isWatchContentStarted = true;
        }
    };

    static logStreamingStartEvent = () => {
        QoEAppLogger.streamingSessionCount = QoEAppLogger.sessionCount;
        QoEAppLogger.sessionCount += 1;
        const now = Date.now();
        if (now > QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND) {
            QoEAppLogger.mediaStats.videoStartTime =
                now - QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND;
        }
        QoEAppLogger.updateContentPayloadFromStream();
        let payload = QoEAppLogger.getContentPayload();
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        delete payload.getProperties().watchContentTransaction;
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logStreamingStart(payload);

        QoEAppLogger.isStreamingStarted = true;
    };

    static logSessionCreatedEvent = () => {
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STARTING;
        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logSessionCreated(payload);
    };

    static logVideoPlayerOpenStreamEvent = () => {
        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logVideoPlayerOpenStream(payload);
    };

    static logFirstFrameEvent = () => {
        const now = Date.now();
        // update firstFrame attributes
        if (now > QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND) {
            QoEAppLogger.mediaStats.videoPlayingStartTime =
                now - QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND;
        }
        // set streamingState to streaming from starting
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;

        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logFirstFrame(payload);
    };

    public static updateProfilePlayingTime = () => {
        if (QoEAppLogger.profiles.length) {
            QoEAppLogger.profiles.forEach((profile) => {
                if (profile.playTime > 0) {
                    QoEAppLogger.mediaStats[
                        `profilePlayingTime_${profile.width}x${profile.height}:${profile.ratio}@${profile.bandwidth}`
                    ] = profile.playTime;
                }
            });
        }
    };

    public static resetProfilePlayingTime = () => {
        QoEAppLogger.isAdaptationEventFirstTime = false;
        QoEAppLogger.mediaHealthStartIntervalTime = 0;
        QoEAppLogger.mediaStats.profileShiftCount = 0;

        if (QoEAppLogger.profiles.length) {
            QoEAppLogger.profiles.forEach((profile) => {
                delete QoEAppLogger.mediaStats[
                    `profilePlayingTime_${profile.width}x${profile.height}:${profile.ratio}@${profile.bandwidth}`
                ];
            });
        }
        QoEAppLogger.profiles = [];
    };

    static clearMediaStatsAttributes = () => {
        QoEAppLogger.mediaStats = {
            ...QoEAppLogger.mediaStatsDefautValues,
        };
    };

    static clearContentAttributes = () => {
        QoEAppLogger.content = {
            ...QoEAppLogger.contentDefaultValues,
            drmType: QoEAppLogger.content.drmType,
            containerFmt: QoEAppLogger.content.containerFmt,
        };
    };

    static clearStateAttributes = () => {
        QoEAppLogger.state = {
            ...QoEAppLogger.stateDefaultValues,
            videoPlayerURL: QoEAppLogger.state.videoPlayerURL,
        };
    };

    static clearAllAttributes = () => {
        QoEAppLogger.clearMediaStatsAttributes();
        QoEAppLogger.clearContentAttributes();
        QoEAppLogger.clearStateAttributes();
    };

    public static logMediaHealthEvent = () => {
        const stats = QoEAppLogger.getPlayerStats();
        if (stats) {
            QoEAppLogger.updateAllHealthStatsMetrics(stats);
            QoEAppLogger.updateProfileTime(stats);
        }
        QoEAppLogger.updateProfilePlayingTime();
        QoEAppLogger.updateContentPayloadFromStream();
        QoEAppLogger.updateMetricsPlayPositionAndBuffer();
        let payload = QoEAppLogger.getContentPayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getMetricsPayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        QoE.logMediaHealth(payload);
    };

    public static startMediaHealthTimer = () => {
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;
        QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        QoEAppLogger.logMediaHealthEvent();
        QoEAppLogger.qoeMediaHealthTimer = setInterval(() => {
            QoEAppLogger.logMediaHealthEvent();
            QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        }, MEDIA_HEALTH_EVENTS_INTERVAL);
    };

    public static stopMediaHealthTimer = () => {
        clearInterval(QoEAppLogger.qoeMediaHealthTimer);
    };

    private static updateAndGetStatusPayload = () => {
        let payload = QoEAppLogger.getContentPayload();
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getMetricsPayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        payload = payload.merge(QoEAppLogger.getEndStatusPayload());
        const filteredProperties = this.getEndStatusPayload().getProperties();
        AppUtils.filterUnknownOrEmptyOrUndefined(<JSON>filteredProperties);
        const tempPayload = new Payload(filteredProperties);
        payload = payload.merge(tempPayload);
        return payload;
    };

    static logStreamingEndEvent = () => {
        if (!QoEAppLogger.isStreamingStarted) {
            Log.w(
                QoEAppLogger.TAG,
                'Ignored. - Trying to log streamingEnd event without streamingStart ...',
            );
            return;
        }
        const payload = QoEAppLogger.updateAndGetStatusPayload();
        delete payload.getProperties().watchContentTransaction;
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logStreamingEnd(payload);
        // reset streamingStart flag
        QoEAppLogger.isStreamingStarted = false;
    };

    static logWatchContentEnd = () => {
        if (!QoEAppLogger.isWatchContentStarted) {
            Log.w(
                QoEAppLogger.TAG,
                'Ignored. - Trying to log watchContentEnd event without watchContentStart ...',
            );
            return;
        }
        const payload = QoEAppLogger.updateAndGetStatusPayload();
        delete payload.getProperties().streamingSessionTransaction;
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoE.logWatchContentEnd(payload);
        // reset watchContentStart flag
        QoEAppLogger.isWatchContentStarted = false;
        QoEAppLogger.lastTunedChannel = '';
    };

    static setEndReason = (data: EndReasonData) => {
        switch (data.type) {
            case QoEAppLogger.ErrorType.ERROR_STATUS:
            case QoEAppLogger.ErrorType.REASON:
            case QoEAppLogger.ErrorType.USER_CODE:
                QoEAppLogger.updateStatusPayload(data.payload);
                break;
            default:
        }
    };

    static logAdultContent = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.ADULT_CONTENT, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.ADULT_CONTENT,
                reason: 'User tried to play Adult Content',
            },
        });
    };

    static logAudioTrackChanged = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED,
                reason: 'User changed Audio Track.',
            },
        });
    };

    static logChannelChanged = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE,
                reason: QoEAppLogger.getWatchContentEndReason(),
            },
        });
    };

    static logDeviceBlacklisted = (codes?: string) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.BLACKLIST, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.BLACKLIST,
                reason: 'Device is blacklisted',
                userTerminalCodes: codes,
                userFinalCodes: codes,
            },
        });
    };

    static logDeviceRestricted = (code: string) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.DEVICE_RESTRICTED, {
            type: QoEAppLogger.ErrorType.USER_CODE,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                reason: 'Device is Restricted',
                userTerminalCodes: code,
                userFinalCodes: code,
                doneReason: QoEAppLogger.PlaybackEndReasonType.DEVICE_RESTRICTED,
            },
        });
    };

    static logDifferentURI = () => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.PLAYLIST_URI_FROM_SESSION_MANAGER,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason:
                        QoEAppLogger.PlaybackEndReasonType.PLAYLIST_URI_FROM_SESSION_MANAGER,
                    reason: 'sessionCreate URL is different from tuningUri so restarted playback as there is a mismatch.',
                },
            },
        );
    };

    static logLocationError(error: any) {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR, {
            type: QoEAppLogger.ErrorType.ERROR_STATUS,
            payload: {
                errorCode: error.getUiErrorCode(),
                errorLoc: QoEAppLogger.ErrorLocValues.LOCATION,
                errorType: QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR,
                errorNative: JSON.stringify(error),
                errorMsg: error.getDescription(),
                doneReason: QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR,
            },
        });
    }

    static logNetworkLost = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.CANCEL,
                doneReason: QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK,
                reason: 'Network Lost.',
                errorLoc: 'NETWORK',
            },
        });
    };

    static logOutOfHome = (code: string) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.OUT_OF_HOME, {
            type: QoEAppLogger.ErrorType.USER_CODE,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                reason: 'Device is Out of Home',
                userTerminalCodes: code,
                userFinalCodes: code,
                doneReason: QoEAppLogger.PlaybackEndReasonType.OUT_OF_HOME,
            },
        });
    };

    static logOutOfRegion = (code: string) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.OUT_OF_REGION, {
            type: QoEAppLogger.ErrorType.USER_CODE,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                reason: 'Device is Out of Region',
                userTerminalCodes: code,
                userFinalCodes: code,
                doneReason: QoEAppLogger.PlaybackEndReasonType.OUT_OF_REGION,
            },
        });
    };

    static logPlaybackError = (error: any) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR, {
            type: QoEAppLogger.ErrorType.ERROR_STATUS,
            payload: {
                errorCode: error.getUiErrorCode(),
                errorLoc: QoEAppLogger.ErrorLocValues.CONTENT,
                errorType: QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR,
                errorNative: JSON.stringify(error),
                errorMsg: error.getDescription(),
                doneReason: QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR,
                status: QoEAppLogger.PlaybackEndStatus.CANCEL,
                reason: 'Player Error',
            },
        });
    };

    static logPlaybackStopped = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER,
                reason: QoEAppLogger.getWatchContentEndReason(),
            },
        });
    };

    static logProgramEnded = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT,
                reason: 'Program Ended',
            },
        });
    };

    static logSessionError = (error: any) => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR, {
            type: QoEAppLogger.ErrorType.ERROR_STATUS,
            payload: {
                errorCode: error.getUiErrorCode(),
                errorLoc: QoEAppLogger.ErrorLocValues.SESSION,
                errorType: QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR,
                errorNative: JSON.stringify(error),
                errorMsg: error.getDescription(),
                doneReason: QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR,
            },
        });
    };

    static logUnsubscribedChannelPlayed = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.UNSUBSCRIBED, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.UNSUBSCRIBED,
                reason: 'User tried to play unsubscribed content',
            },
        });
    };

    static logVisibilityBackground = () => {
        QoEAppLogger.setEndReasonAndLogEvent(QoEAppLogger.PlaybackEndReasonType.VISIBILITY_BEHIND, {
            type: QoEAppLogger.ErrorType.REASON,
            payload: {
                status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                doneReason: QoEAppLogger.PlaybackEndReasonType.VISIBILITY_BEHIND,
                reason: 'User switched to some other application.',
            },
        });
    };

    static setEndReasonAndLogEvent = (reason: string, data: EndReasonData) => {
        const stats = QoEAppLogger.getPlayerStats();
        if (stats) {
            QoEAppLogger.updateProfileTime(stats);
            QoEAppLogger.updateAllHealthStatsMetrics(stats);
        }
        QoEAppLogger.updateMetricsPlayPositionAndBuffer();
        QoEAppLogger.updateProfilePlayingTime();
        QoEAppLogger.setEndReason(data);
        // only end of content should set isProgramBoundary = true
        if (reason === QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT)
            QoEAppLogger.isProgramBoundary = true;
        else if (
            reason !== QoEAppLogger.PlaybackEndReasonType.UNSUBSCRIBED ||
            QoEAppLogger.PlaybackEndReasonType.BLACKLIST
        )
            QoEAppLogger.logStreamingEndEvent();
        // watchContentEnd should not be called on audio track changed.
        if (reason !== QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED)
            QoEAppLogger.logWatchContentEnd();

        if (
            reason !== QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED &&
            reason !== QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT &&
            reason !== QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK
        )
            QoEAppLogger.clearAllAttributes();

        if (
            reason === QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE ||
            reason === QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER
        ) {
            QoEAppLogger.state.videoPlayerURL = '';
            QoEAppLogger.content.drmType = '';
            QoEAppLogger.content.containerFmt = QoEContainerFormatType.UNKNOWN;
        }
    };

    // ********************************************************************
    // set QOE params global - {base, population}
    //                content
    //                metrics
    //                state
    // ********************************************************************

    private static getMetricsPayload() {
        return new Payload(QoEAppLogger.mediaStats);
    }

    private static getEndStatusPayload() {
        return new Payload(QoEAppLogger.endStatus);
    }

    private static getContentPayload() {
        return new Payload(QoEAppLogger.content);
    }

    private static getPlayerStatePayload() {
        return new Payload(QoEAppLogger.state);
    }

    public static createProfiles = (variants) => {
        if (variants?.length) {
            variants.forEach((variant) => {
                const obj: Profile = {
                    width: variant.width,
                    height: variant.height,
                    playTime: 0,
                    ratio: Math.floor(variant.width / variant.height),
                    bandwidth: variant.bandwidth,
                };
                QoEAppLogger.profiles.push(obj);
            });

            let profilesArray = [...QoEAppLogger.profiles];
            profilesArray = profilesArray.filter((profile, index, arr) => {
                return index === arr.findIndex((p) => p.bandwidth === profile.bandwidth);
            });

            QoEAppLogger.profiles = [...profilesArray];
        }
    };

    public static updateProfileTime = (variant?: any, timeStamp?: number) => {
        const variantObj = variant || QoEAppLogger.getPlayerStats();
        const intervalTime =
            QoEAppLogger.mediaHealthStartIntervalTime || QoEAppLogger.watchContentStartTime;
        const time = timeStamp || Date.now() - intervalTime;
        if (variantObj && QoEAppLogger.profiles.length) {
            const variantBandwidth = variantObj.streamBandwidth || variantObj.bandwidth;
            QoEAppLogger.profiles.forEach((profile: Profile, index) => {
                if (variantBandwidth && variantBandwidth === profile.bandwidth) {
                    QoEAppLogger.profiles[index].playTime += time;
                }
            });
        }
    };

    private static getContainerFmt(containerFmt: TransportEncodingType) {
        switch (containerFmt) {
            case TransportEncodingType.DashFmp4TransportStream:
                return QoEContainerFormatType.DASHFMP4TS;
            case TransportEncodingType.DashTransportStream:
                return QoEContainerFormatType.DASHTS;
            case TransportEncodingType.HlsFmp4TransportStream:
                return QoEContainerFormatType.HLSFMP4TS;
            case TransportEncodingType.Mpeg2TransportStream:
                return QoEContainerFormatType.MPEG;
            case TransportEncodingType.HlsTransportStream:
                return QoEContainerFormatType.HLSTS;
            case TransportEncodingType.SmoothTransportStream:
                return QoEContainerFormatType.SMOOTHTS;
            default:
                return QoEContainerFormatType.UNKNOWN;
        }
    }

    private static getStreamingProtocol(url = '') {
        let protocol = QoEStreamingProtocolType.UNKNOWN;
        if (url.includes(DASH_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.DASH;
        } else if (url.includes(HLS_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.HLS;
        } else if (url.includes(SMOOTH_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.SMOOTH_STREAMING;
        }
        return protocol;
    }

    private static getPlayerConfigurationPayload() {
        QoEAppLogger.configuration.videoPlayer = this.configuration.videoPlayer;
        QoEAppLogger.configuration.videoSource = this.configuration.videoSource;
        QoEAppLogger.configuration.assetBounds = this.configuration.assetBounds;
        QoEAppLogger.configuration.assetBounds = this.configuration.assetBounds;
        QoEAppLogger.configuration.streamType = this.configuration.streamType;
        QoEAppLogger.configuration.connection =
            DeviceModel.getTVProperties().getActiveConnectionType();
        return new Payload(QoEAppLogger.configuration);
    }

    public static getWatchContentEndReason = () => {
        return QoEAppLogger.watchContentEndReason;
    };

    private static clearStatusPayload() {
        QoEAppLogger.endStatus.errorCode = '';
        QoEAppLogger.endStatus.errorLoc = '';
        QoEAppLogger.endStatus.errorType = '';
        QoEAppLogger.endStatus.errorNative = '';
        QoEAppLogger.endStatus.errorMsg = '';
        QoEAppLogger.endStatus.errorUrl = '';
        QoEAppLogger.endStatus.status = '';
        QoEAppLogger.endStatus.reason = '';
        QoEAppLogger.endStatus.doneReason = '';
        QoEAppLogger.endStatus.userTerminalCodes = '';
        QoEAppLogger.endStatus.userFinalCodes = '';
    }

    public static setWatchContentEndReason = (value: string) => {
        QoEAppLogger.watchContentEndReason = value;
    };

    static updateStatusPayload(data: IQoEStatus) {
        const {
            errorCode,
            errorLoc,
            errorMsg,
            errorType,
            errorNative,
            status,
            reason,
            doneReason,
            userFinalCodes,
            userTerminalCodes,
        } = data;
        QoEAppLogger.clearStatusPayload();
        QoEAppLogger.endStatus.errorCode = errorCode;
        QoEAppLogger.endStatus.errorLoc = errorLoc;
        QoEAppLogger.endStatus.errorType = errorType;
        QoEAppLogger.endStatus.errorNative = errorNative;
        QoEAppLogger.endStatus.errorMsg = errorMsg;
        QoEAppLogger.endStatus.errorUrl = QoEAppLogger.state.videoPlayerURL;
        QoEAppLogger.endStatus.status = status;
        QoEAppLogger.endStatus.reason = reason;
        QoEAppLogger.endStatus.doneReason = doneReason;
        QoEAppLogger.endStatus.userTerminalCodes = userTerminalCodes;
        QoEAppLogger.endStatus.userFinalCodes = userFinalCodes;
    }

    static updateMetricsPlayPositionAndBuffer() {
        const currentTime = QoEAppLogger.shakaPlayerInstance?.getCurrentTimeInMs() || 0;
        const endTime = QoEAppLogger.shakaPlayerInstance?.getSeekRangeEndTimeInMs() || 0;
        QoEAppLogger.mediaStats.playPosition = Math.floor(currentTime);
        if (endTime > currentTime) {
            QoEAppLogger.mediaStats.videoBufferAvailable =
                (endTime - currentTime) / MILLISECONDS_IN_A_SECOND;
        }
    }

    static updateMetricsVideoBufferAvailable(availableBufferTime: number) {
        QoEAppLogger.mediaStats.videoBufferAvailable = availableBufferTime;
    }

    static updateConfigurationForRegionMode(regionMode: QoERegionModeType) {
        QoEAppLogger.configuration.regionMode = regionMode;
    }

    static updateConfigurationForConnectionMode(connectionMode: QoEConnectionModeType) {
        QoEAppLogger.configuration.connectionMode = connectionMode;
    }

    static updateContentPayload(
        channelData: Channel,
        airingData: AiringOffer[],
        vodPreviewData?: TOfferPreviewCustomData,
    ) {
        QoEAppLogger.content.channelId = channelData.stationId || '';
        QoEAppLogger.content.channelName = channelData.name || '';
        QoEAppLogger.content.channelNum = <string>(channelData.channelNumber || '');
        QoEAppLogger.content.contentId =
            airingData[0]?.logging?.contentId || vodPreviewData?.contentPreview?.contentId || '';
        QoEAppLogger.content.contentTitle =
            airingData[0]?.banner?.title || vodPreviewData?.contentPreview?.title || '';
        QoEAppLogger.content.collectionId =
            airingData[0]?.logging?.collectionId ||
            vodPreviewData?.contentPreview?.collectionId ||
            '';
        QoEAppLogger.content.collectiontitle =
            airingData[0]?.banner?.title || vodPreviewData?.contentPreview?.title || '';
        QoEAppLogger.content.channelCallSign = channelData.callSign || '';
        const resolution = channelData.videoResolution || '';
        QoEAppLogger.content.maxRes = resolution.toLocaleUpperCase();
    }

    static updateStreamingUrl(url) {
        QoEAppLogger.state.videoPlayerURL = url || '';
        QoEAppLogger.configuration.streamingProtocol = QoEAppLogger.getStreamingProtocol(
            QoEAppLogger.state.videoPlayerURL,
        );
    }

    static updateContentPayloadFromStream() {
        const variant = QoEAppLogger.shakaPlayerInstance?.getCurrentAudioTrack() || null;
        if (variant) {
            const { videoCodec, audioCodec } = variant;
            QoEAppLogger.content.videoCodec = videoCodec || '';
            QoEAppLogger.content.audioCodec = audioCodec || '';
        }
    }

    static updateContentPayloadDrmAndContainerInfo(drm, containerFmt: TransportEncodingType) {
        QoEAppLogger.content.drmType = drm.charAt(0).toUpperCase() + drm.slice(1) || '';
        QoEAppLogger.content.containerFmt = QoEAppLogger.getContainerFmt(containerFmt);
    }

    // compute mediaHealth metrics and update

    static updateHealthStatsMetricsForTrickPlay(startTime: number, endTime: number) {
        if (!QoEAppLogger.trickplayStartTime && startTime) {
            QoEAppLogger.trickplayStartTime = startTime;
        } else if (QoEAppLogger.trickplayStartTime && endTime) {
            QoEAppLogger.mediaStats.videoTrickplayingCount += 1;
            QoEAppLogger.mediaStats.videoTrickplayingTime += Math.abs(
                endTime - QoEAppLogger.trickplayStartTime,
            );
            QoEAppLogger.trickplayStartTime = 0;
        }
    }

    static updateHealthStatsMetricsByEvent(event: any) {
        // Ignore duplicate events
        if (
            QoEAppLogger.previousPlayerEvent &&
            event.type === QoEAppLogger.previousPlayerEvent.type
        ) {
            Log.i(
                QoEAppLogger.TAG,
                `Ignoring event: "${event.type}" because it is a duplicate of the previous event and it will not be collected.`,
            );
            return;
        }

        switch (event.type) {
            case QoEAppLogger.PlayerEvents.PLAY:
                QoEAppLogger.mediaStats.videoPlayingCount += 1;
                break;
            case QoEAppLogger.PlayerEvents.PAUSE:
                QoEAppLogger.mediaStats.videoPausingCount += 1;
                break;
            case QoEAppLogger.PlayerEvents.BUFFERING:
                QoEAppLogger.mediaStats.videoBufferingCount += 1;
                break;
            case QoEAppLogger.PlayerEvents.STALLED:
                QoEAppLogger.mediaStats.videoStallingCount += 1;
                break;
            default:
        }

        QoEAppLogger.aggregateTimeDifferenceToPreviousEvent(event);
        // remember last event
        QoEAppLogger.previousPlayerEvent = event;
    }

    private static aggregateTimeDifferenceToPreviousEvent(event?: Event) {
        if (!QoEAppLogger.previousPlayerEvent) {
            // valid when function called before first event is received
            return;
        }
        const timeDiff = QoEAppLogger.getPlayerEventTimeDifference(event);
        switch (QoEAppLogger.previousPlayerEvent.type) {
            case QoEAppLogger.PlayerEvents.PAUSE:
                QoEAppLogger.mediaStats.videoPausingTime += timeDiff;
                break;
            case QoEAppLogger.PlayerEvents.STALLED:
                QoEAppLogger.mediaStats.videoStallingTime += timeDiff;
                break;
            default:
        }
    }

    private static getPlayerEventTimeDifference(event?: Event): number {
        let timeDiff;
        if (event) {
            timeDiff = event.timeStamp - QoEAppLogger.previousPlayerEvent.timeStamp;
        } else {
            // workaround event.timestamp retuns relative time
            // convert epoch time to relative time by (Date.now() - qoeStartTime)
            timeDiff =
                Date.now() -
                (QoEAppLogger.qoeStartTime + QoEAppLogger.previousPlayerEvent.timeStamp);
        }
        return timeDiff;
    }

    private static convertBitsToMbps(value: number) {
        return (
            Math.round((value / MILLISECONDS_IN_A_SECOND / MILLISECONDS_IN_A_SECOND) * 100) / 100
        ); // Mbps
    }

    static updateAllHealthStatsMetrics(stats: any) {
        const decodedFrames = stats.decodedFrames ? parseInt(stats.decodedFrames, 10) : 0;
        const droppedFrames = stats.droppedFrames ? parseInt(stats.droppedFrames, 10) : 0;
        const numberOfLevelsPlayed = stats.switchHistory.length || 0;

        QoEAppLogger.mediaStats.videoFramesPresented = decodedFrames;
        QoEAppLogger.mediaStats.videoFramesDropped = droppedFrames;

        if (stats.playTime) {
            QoEAppLogger.mediaStats.videoPlayingTime = Math.round(
                stats.playTime * MILLISECONDS_IN_A_SECOND,
            ); // msec
        }
        // bufferingTime is equal to framesPresentedtime + playerLaterncytime + bufferringtime
        if (stats.bufferingTime) {
            QoEAppLogger.mediaStats.videoBufferingTime = Math.round(
                stats.bufferingTime * MILLISECONDS_IN_A_SECOND +
                    QoEAppLogger.mediaStats.videoStartTime,
            );
        }
        const estimatedBandwidth = stats.estimatedBandwidth || 0;
        QoEAppLogger.mediaStats.networkBitrate = QoEAppLogger.convertBitsToMbps(estimatedBandwidth);
        const videoBitrate = stats.streamBandwidth || 0;
        QoEAppLogger.mediaStats.videoBitrate = QoEAppLogger.convertBitsToMbps(videoBitrate);

        let totalLevelsBandwidth = 0;
        if (stats.switchHistory) {
            totalLevelsBandwidth = stats.switchHistory.reduce((accumulator, level) => {
                return accumulator + level.bandwidth;
            }, 0);
        }

        // currently shakaPlayer does not provide levels of data download so updating
        // estimated bandwidth as avgNetworkBitrate
        QoEAppLogger.mediaStats.avgNetworkBitrate = QoEAppLogger.mediaStats.networkBitrate;

        // Transform the size in Mb.
        // Use 1000 and NOT 1024 because of the difference between Mb (1000Kb) and MiB (1024KiB)
        // Do a check to avoid disivion by 0
        if (numberOfLevelsPlayed > 0) {
            QoEAppLogger.mediaStats.avgVideoBitrate = QoEAppLogger.convertBitsToMbps(
                totalLevelsBandwidth / numberOfLevelsPlayed,
            );
        }

        // collect metrics till now with dummy event
        QoEAppLogger.aggregateTimeDifferenceToPreviousEvent();
    }
}
